import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useCart } from '../../context/CartContext';
import { auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { API_BASE_URL } from '../../constants';

function CheckoutSuccessPage() {
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const isFree = searchParams.get('free');

    // Free purchases are already fulfilled server-side
    if (isFree) {
      setVerified(true);
      return;
    }

    // Paid purchase — wait for Firebase Auth to initialise, then verify
    if (sessionId) {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user === undefined) return; // still loading
        unsubscribe(); // only need the first resolved state

        setVerifying(true);
        try {
          const token = await user?.getIdToken();
          if (!token) {
            setError('Not signed in — please sign in and visit your library.');
            setVerifying(false);
            return;
          }
          const res = await fetch(`${API_BASE_URL}/api/verify-session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ sessionId }),
          });
          const data = await res.json();
          if (res.ok && data.success) {
            setVerified(true);
            clearCart();           // clear client-side cart state
          } else {
            setError(data.error || 'Verification failed');
          }
        } catch (err) {
          console.error('Verify error:', err);
          setError('Could not verify payment. Your purchase may still be processing.');
        }
        setVerifying(false);
      });
    } else {
      // No session_id and not free — shouldn't normally happen
      setVerified(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div style={{
      minHeight: '70vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      textAlign: 'center',
    }}>
      {verifying ? (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
          <h1 style={{ color: '#f8fafc', marginBottom: '0.5rem' }}>Confirming your purchase…</h1>
          <p style={{ color: '#94a3b8' }}>Please wait while we verify your payment with Stripe.</p>
        </>
      ) : error ? (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <h1 style={{ color: '#f8fafc', marginBottom: '0.5rem' }}>Verification Issue</h1>
          <p style={{ color: '#f87171', marginBottom: '2rem', maxWidth: '480px' }}>{error}</p>
          <Link to="/library" style={{
            padding: '0.7rem 1.5rem',
            borderRadius: '10px',
            background: 'linear-gradient(140deg, #3b82f6, #2563eb)',
            color: '#fff',
            fontWeight: 600,
            textDecoration: 'none',
          }}>
            Check My Library
          </Link>
        </>
      ) : (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h1 style={{ color: '#f8fafc', marginBottom: '0.5rem' }}>Purchase Successful!</h1>
          <p style={{ color: '#94a3b8', marginBottom: '2rem', maxWidth: '480px' }}>
            Your projects are now available in your library. You can download them at any time.
          </p>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Link to="/library" style={{
              padding: '0.7rem 1.5rem',
              borderRadius: '10px',
              background: 'linear-gradient(140deg, #3b82f6, #2563eb)',
              color: '#fff',
              fontWeight: 600,
              textDecoration: 'none',
            }}>
              Go to My Library
            </Link>
            <Link to="/marketplace" style={{
              padding: '0.7rem 1.5rem',
              borderRadius: '10px',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              color: '#e2e8f0',
              fontWeight: 600,
              textDecoration: 'none',
            }}>
              Back to Marketplace
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

export default CheckoutSuccessPage;
